import * as React from "react";
import styles from "./Pagination.module.css";
import { cn } from "../../../utils/cn";
import { Button } from "../Button/Button";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn(styles.pagination, className)}
      {...props}
    />
  );
};

const PaginationContent = ({
  className,
  ...props
}: React.ComponentProps<"ul">) => {
  return (
    <ul
      data-slot="pagination-content"
      className={cn(styles.content, className)}
      {...props}
    />
  );
};

const PaginationItem = ({ ...props }: React.ComponentProps<"li">) => {
  return <li data-slot="pagination-item" {...props} />;
};

const PaginationButton = ({
  className,
  isActive,
  size = "icon",
  disabled,
  ...props
}: React.ComponentProps<typeof Button> & {
  isActive?: boolean;
}) => {
  return (
    <Button
      type="button"
      aria-current={isActive ? "page" : undefined}
      data-slot="pagination-button"
      data-active={isActive}
      variant={isActive ? "default" : "outline"}
      size={size}
      disabled={disabled}
      className={cn(styles.link, className)}
      {...props}
    />
  );
};

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationButton>) => {
  return (
    <PaginationButton
      aria-label="Go to previous page"
      size="default"
      className={cn(styles.previous, className)}
      {...props}
    >
      <ChevronLeft />
      <span className={styles.text}>Previous</span>
    </PaginationButton>
  );
};

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationButton>) => {
  return (
    <PaginationButton
      aria-label="Go to next page"
      size="default"
      className={cn(styles.next, className)}
      {...props}
    >
      <span className={styles.text}>Next</span>
      <ChevronRight />
    </PaginationButton>
  );
};

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn(styles.ellipsis, className)}
      {...props}
    >
      <MoreHorizontal className={styles.ellipsisIcon} />
    </span>
  );
};

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationButton,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
